import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useProgramaBySlug, useSedes } from "../hooks/useCms";
import { programs as staticPrograms } from "../data/programs";
import { sedes as staticSedes } from "../data/sedes";
import { sedeCmsToCard } from "../lib/adapters";
import { mediaUrl } from "../lib/strapi";
import { BlocksRenderer } from "../components/BlocksRenderer";
import { Lightbox } from "../components/Lightbox";
import { SedeMapaLink } from "../components/SedeMapaLink";
import type { ProgramAccent } from "../data/programs";
import { NotFoundPage } from "./NotFoundPage";

const accentTextDeep: Record<ProgramAccent, string> = {
  leaf: "text-accent-leaf-deep",
  cobalt: "text-accent-cobalt-deep",
  sun: "text-accent-sun-deep",
  violet: "text-accent-violet-deep",
};

const accentBorder: Record<ProgramAccent, string> = {
  leaf: "border-accent-leaf",
  cobalt: "border-accent-cobalt",
  sun: "border-accent-sun",
  violet: "border-accent-violet",
};

// Etiquetas humanas para el enum `estado` del CMS.
const estadoLabel: Record<"proximamente" | "abierta" | "cerrada" | "finalizada", string> = {
  proximamente: "Próximamente",
  abierta: "Abierta",
  cerrada: "Cerrada",
  finalizada: "Finalizada",
};

function formatRangoEdicion(inicio?: string, fin?: string): string {
  const fmt = (d?: string) =>
    d
      ? new Date(d).toLocaleDateString("es-ES", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "";
  const a = fmt(inicio);
  const b = fmt(fin);
  if (a && b) return `${a} – ${b}`;
  return a || b;
}

export function ProgramaDetallePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: cmsPrograma, isLoading } = useProgramaBySlug(slug ?? "");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Fallback al dataset estático mientras el CMS aún no esté sembrado.
  const staticPrograma = slug ? staticPrograms.find((p) => p.slug === slug) : undefined;

  if (!slug) return <NotFoundPage />;
  if (isLoading && !staticPrograma) {
    return (
      <section className="container-emjc py-20">
        <p className="text-gray-700">Cargando…</p>
      </section>
    );
  }
  if (!cmsPrograma && !staticPrograma) return <NotFoundPage />;

  const nombre = cmsPrograma?.nombre ?? staticPrograma!.name;
  const eslogan = cmsPrograma?.eslogan ?? staticPrograma!.short;
  const descripcionCorta = cmsPrograma?.descripcionCorta ?? staticPrograma!.long;
  const accent: ProgramAccent =
    (cmsPrograma?.acentoColor as ProgramAccent | undefined) ?? staticPrograma?.accent ?? "leaf";

  // Solo Proyecto Verano muestra sedes (es el único con campamentos urbanos
  // simultáneos en las cuatro localidades). Cargamos de CMS si hay; si no,
  // del dataset estático.
  const muestraSedes = slug === "proyecto-verano";

  return (
    <>
      <Helmet>
        <title>{nombre} — Campus EMJC</title>
        <meta name="description" content={descripcionCorta} />
      </Helmet>

      <article className="py-20">
        <div className="container-emjc">
          <p className={`text-sm font-semibold uppercase tracking-widest ${accentTextDeep[accent]}`}>
            Programa
          </p>
          <h1 className="mt-2 max-w-3xl font-display text-5xl font-semibold leading-tight md:text-6xl">
            {nombre}
          </h1>
          {eslogan && (
            <p className="mt-4 max-w-2xl font-display text-2xl text-gray-700">
              {eslogan}
            </p>
          )}
          <p className="mt-6 max-w-3xl text-lg text-gray-700">{descripcionCorta}</p>
        </div>

        {/* Imagen de portada del programa (hero 16:9). Sólo si el admin la
            ha subido al campo `imagenPortada`. */}
        {cmsPrograma?.imagenPortada?.url && (
          <div className="container-emjc mt-12">
            <figure className="overflow-hidden rounded-3xl border border-gray-300 bg-white">
              <img
                src={mediaUrl(cmsPrograma.imagenPortada.url)}
                alt={cmsPrograma.imagenPortada.alternativeText ?? nombre}
                className="aspect-[16/9] w-full object-cover"
                loading="lazy"
              />
            </figure>
          </div>
        )}

        {/* Descripción larga (bloques editables desde el admin: párrafos,
            negritas, listas, enlaces, imágenes intercaladas, etc.). */}
        {cmsPrograma?.descripcionLarga && cmsPrograma.descripcionLarga.length > 0 && (
          <section className="container-emjc mt-12">
            <BlocksRenderer blocks={cmsPrograma.descripcionLarga} />
          </section>
        )}

        {muestraSedes && <SedesProyectoVerano accent={accent} />}

        {cmsPrograma?.ediciones && cmsPrograma.ediciones.length > 0 && (
          <section className="container-emjc mt-20">
            <h2 className="font-display text-3xl font-semibold md:text-4xl">
              Ediciones
            </h2>
            <ul className="mt-8 grid gap-4 md:grid-cols-2">
              {cmsPrograma.ediciones
                // Más recientes primero — el equipo suele querer ver la edición
                // del año en curso arriba de todo.
                .slice()
                .sort((a, b) => b.anio - a.anio)
                .map((e) => (
                  <li key={e.documentId ?? e.id}>
                    <Link
                      to={`/programas/${slug}/${e.slug}`}
                      className={`flex gap-4 overflow-hidden rounded-2xl border-l-4 ${accentBorder[accent]} bg-white ring-1 ring-gray-300 transition hover:ring-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange`}
                    >
                      {e.imagenPortada?.url && (
                        <img
                          src={mediaUrl(e.imagenPortada.url)}
                          alt={e.imagenPortada.alternativeText ?? `Edición ${e.anio}`}
                          className="hidden h-auto w-40 shrink-0 self-stretch object-cover sm:block"
                          loading="lazy"
                        />
                      )}
                      <div className="flex-1 p-5">
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="font-display text-xl font-semibold">
                            {e.titulo?.trim() || `Edición ${e.anio}`}
                          </p>
                          <span className="text-xs uppercase tracking-widest text-gray-700">
                            {estadoLabel[e.estado]}
                          </span>
                        </div>
                        {e.titulo?.trim() && (
                          <p className="mt-1 text-xs uppercase tracking-widest text-gray-700">
                            Edición {e.anio}
                          </p>
                        )}
                        {(e.fechaInicio || e.fechaFin) && (
                          <p className="mt-2 text-sm text-gray-700">
                            {formatRangoEdicion(e.fechaInicio, e.fechaFin)}
                          </p>
                        )}
                        <p className="mt-3 text-sm font-semibold text-brand-orange-deep">
                          Ver detalle →
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
            </ul>
          </section>
        )}

        {/* Galería de portada del programa: rejilla con lightbox accesible.
            Sólo se renderiza si el admin ha subido fotos al campo
            `galeriaPortada`. */}
        {cmsPrograma?.galeriaPortada && cmsPrograma.galeriaPortada.length > 0 && (
          <section className="container-emjc mt-20" aria-labelledby="programa-galeria-heading">
            <h2
              id="programa-galeria-heading"
              className="font-display text-3xl font-semibold md:text-4xl"
            >
              {nombre} en imágenes
            </h2>
            <ul className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {cmsPrograma.galeriaPortada.map((foto, idx) => {
                const thumbSrc =
                  mediaUrl(foto.formats?.medium?.url) ??
                  mediaUrl(foto.formats?.small?.url) ??
                  mediaUrl(foto.url);
                return (
                  <li key={foto.documentId ?? foto.id}>
                    <button
                      type="button"
                      onClick={() => setLightboxIndex(idx)}
                      aria-label={
                        foto.alternativeText
                          ? `Abrir imagen: ${foto.alternativeText}`
                          : `Abrir imagen ${idx + 1} de ${cmsPrograma.galeriaPortada!.length}`
                      }
                      className="group block aspect-square w-full overflow-hidden rounded-lg bg-brand-paper ring-1 ring-gray-300 transition hover:ring-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2"
                    >
                      <img
                        src={thumbSrc}
                        alt={foto.alternativeText ?? ""}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* Volver — al final, no al principio: un usuario que aterriza en
            un programa quiere ver la información antes que el back link. */}
        <div className="container-emjc mt-20">
          <Link
            to="/programas"
            className="text-sm font-semibold text-brand-ink hover:text-brand-orange-deep hover:underline"
          >
            ← Volver a todos los programas
          </Link>
        </div>
      </article>

      {lightboxIndex !== null && cmsPrograma?.galeriaPortada && cmsPrograma.galeriaPortada.length > 0 && (
        <Lightbox
          fotos={cmsPrograma.galeriaPortada}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </>
  );
}

function SedesProyectoVerano({ accent }: { accent: ProgramAccent }) {
  const { data: cmsSedes } = useSedes();
  const sedes =
    cmsSedes && cmsSedes.length > 0 ? cmsSedes.map(sedeCmsToCard) : staticSedes;

  return (
    <section className="container-emjc mt-20">
      <p className={`text-sm font-semibold uppercase tracking-widest ${accentTextDeep[accent]}`}>
        Cuatro sedes simultáneas en julio
      </p>
      <h2 className="mt-2 max-w-2xl font-display text-3xl font-semibold md:text-4xl">
        Donde está la universidad, está la ciencia.
      </h2>
      <p className="mt-4 max-w-2xl text-gray-700">
        Los campamentos urbanos del Proyecto Verano se imparten a la vez en
        nuestras cuatro localidades EMJC. Cada familia se inscribe en la sede
        que mejor le encaje.
      </p>

      <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        {sedes.map((s) => (
          <div
            key={s.city}
            className="overflow-hidden rounded-2xl border border-gray-300 bg-white transition-colors hover:border-brand-orange"
          >
            {s.imageUrl && (
              <img
                src={mediaUrl(s.imageUrl)}
                alt={s.city}
                className="aspect-[4/3] w-full object-cover"
                loading="lazy"
              />
            )}
            <div className="p-6">
              <h3 className="font-display text-xl font-semibold">{s.city}</h3>
              <p className="mt-1 text-sm font-medium text-brand-orange-deep">
                {s.venue}
              </p>
              <p className="mt-3 text-sm text-gray-700">{s.shortDescription}</p>
              {s.mapUrl && (
                <div className="mt-4">
                  <SedeMapaLink enlaceMapa={s.mapUrl} ciudad={s.city} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
